import { RefundProtectQuoteRequest } from 'api/insurance'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, TWENTY_ONE_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import { getFlexibleCancellationDays, isFlexibleCancellationPolicy, isWithinSevenDays } from 'lib/offer/cancellationPolicyUtils'
import moment from 'moment'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import config from 'constants/config'
import { CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'

interface CancellationPolicyInfo {
  cancellationType: 'free' | 'flexible' | undefined
  cancelUntilFullRefundDate: moment.Moment
  cancelAfterNonRefundableDate: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean;
}

export const getBookingProtectionQuoteKey = (quotePayload: RefundProtectQuoteRequest | null): string => {
  if (!quotePayload) {
    return 'default'
  }

  return Object.keys(quotePayload).reduce((acc, key) => {
    return `${acc}${key}=${quotePayload[key]};`
  }, '')
}

const showFreeCancellation = (
  checkInDate: string | moment.Moment | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
) => {
  if (!cancellationPolicy) {
    return false
  }
  if (cancellationPolicy.type !== 'post-purchase-seven-days') {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    return moment(checkInDate).subtract(21, 'days').isAfter(moment().startOf('day'))
  }
}

const showFlexibleCancellation = (
  checkInDate: string | moment.Moment | undefined,
  cancellationPolicy: App.OfferCancellationPolicy | undefined,
) => {
  if (!cancellationPolicy) {
    return false
  }
  if (!isFlexibleCancellationPolicy(cancellationPolicy)) {
    return false
  }
  if (!checkInDate) {
    return true
  } else {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    const isWithinChangeOfMind = isWithinSevenDays(moment(), moment(checkInDate), moment())
    return isWithinChangeOfMind || moment(checkInDate).subtract(days, 'days').isAfter(moment().startOf('day'))
  }
}

export const isBookingProtectionAvailableForCartItems = (cartItems: Array<App.Checkout.Item>, offers: { [offerId: string]: App.Offer }): boolean => {
  for (const cartItem of cartItems) {
    if (!isEnabledForFeature(config.BOOKING_PROTECTION_ENABLED_LIST, cartItem.itemType)) {
      return false
    }

    if (cartItem.itemType === CHECKOUT_ITEM_TYPE_LE_HOTEL) {
      const offer = offers[cartItem.offerId]
      if (offer && !isBookingProtectionAvailableForAccommodationOffer(offer)) {
        return false
      }
    }
  }

  return true
}

const isBookingProtectionAvailableForAccommodationOffer = (offer: App.Offer): boolean => {
  if (offer.parentType === OFFER_TYPE_HOTEL) {
    if (offer.productType === OFFER_TYPE_ALWAYS_ON) return true
    if (offer.productType === OFFER_TYPE_HOTEL) return true
    if (offer.productType === OFFER_TYPE_BED_BANK) return true
  }

  return false
}

export const getCancellationPolicyInfo = (checkInDate: string | undefined, cancellationPolicy: App.OfferCancellationPolicy | undefined): CancellationPolicyInfo => {
  const freeCancellation = showFreeCancellation(checkInDate, cancellationPolicy)
  const flexibleCancellation = showFlexibleCancellation(checkInDate, cancellationPolicy)
  const cancellationType = freeCancellation ? 'free' : flexibleCancellation ? 'flexible' : undefined
  let startBasedOnToday: moment.Moment = moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let cancelUntilFullRefundDate: moment.Moment = moment()
  let cancelAfterNonRefundableDate: moment.Moment | undefined
  let cancellationAfterChangeOfMindPeriod = false

  if (freeCancellation) {
    const startBasedOnCheckIn = checkInDate && moment(checkInDate).subtract(TWENTY_ONE_DAYS_BEFORE_CHECK_IN, 'days')
    if (startBasedOnCheckIn) {
      cancelUntilFullRefundDate = moment.min(startBasedOnToday, startBasedOnCheckIn)
    }
  } else if (flexibleCancellation && cancellationPolicy && checkInDate) {
    const days = getFlexibleCancellationDays(cancellationPolicy.type)
    let startBasedOnCheckIn = moment(checkInDate).subtract(days, 'days')

    const changeOfMindDeadline = moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

    cancellationAfterChangeOfMindPeriod = startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days'))
    startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

    cancelUntilFullRefundDate = startBasedOnToday
    cancelAfterNonRefundableDate = startBasedOnCheckIn
  }

  return {
    cancellationType, cancelUntilFullRefundDate, cancelAfterNonRefundableDate, cancellationAfterChangeOfMindPeriod,
  }
}

export const bookingProtectionCancellationReasons = [
  'Illness/injury (including Covid)',
  'Home emergency',
  'Pre-existing medical condition',
  'Flight disruption',
  'Adverse weather',
  'Theft of documents',
  'Workplace redundancy',
  'Jury service',
  'Court summons',
  'Pregnancy complications',
  'Death of immediate family member',
  'Mechanical breakdown',
  'Relocated for work',
  'Armed forces & emergency services recall',
  'Changes to examination dates',
]
